import { useEffect, useMemo, useRef, useState } from "react";
import { formatLocationOption, groupLocationsForDropdown } from "../utils/locationGroups";

function LocationDropdown({
  value,
  onChange,
  locations,
  placeholder = "Select a location...",
  includeOtherOption = true,
  otherLabel = "Type full address manually",
  includeMapsLinkOption = true,
  mapsLinkValue = "MAPS_LINK",
  mapsLinkLabel = "Paste Google Maps link (outside service area)",
  id,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const groups = useMemo(() => groupLocationsForDropdown(locations || []), [locations]);

  const selectedLabel = useMemo(() => {
    if (!value) return placeholder;
    if (value === mapsLinkValue) return mapsLinkLabel;
    if (value === "OTHER") return otherLabel;
    const selected = (locations || []).find((loc) => loc.name === value);
    return selected ? formatLocationOption(selected) : value;
  }, [locations, mapsLinkLabel, mapsLinkValue, otherLabel, placeholder, value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div className="mama-gf-dropdown" ref={rootRef}>
      <button
        id={id}
        type="button"
        className={`mama-gf-trigger ${open ? "is-open" : ""} ${value ? "has-value" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="mama-gf-trigger-value">{selectedLabel}</span>
        <span className="mama-gf-trigger-chevron" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="mama-gf-panel" role="listbox" aria-labelledby={id}>
          <div className="mama-gf-panel-scroll">
            {groups.map((group) => (
              <div key={group.label} className="mama-gf-group">
                <p className="mama-gf-group-title">{group.label}</p>
                {group.options.map((loc) => (
                  <button
                    key={loc.id}
                    type="button"
                    className={`mama-gf-option ${value === loc.name ? "is-selected" : ""}`}
                    onClick={() => handleSelect(loc.name)}
                    role="option"
                    aria-selected={value === loc.name}
                  >
                    <span className="mama-gf-option-title">{loc.name}</span>
                    <span className="mama-gf-option-meta">
                      {Number.parseFloat(loc.delivery_fee || 0) === 0
                        ? "Free delivery"
                        : `RM ${Number.parseFloat(loc.delivery_fee).toFixed(2)} delivery`}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>

          {includeOtherOption && (
            <div className="mama-gf-footer">
              {includeMapsLinkOption && (
                <button
                  type="button"
                  className={`mama-gf-option mama-gf-option-other ${value === mapsLinkValue ? "is-selected" : ""}`}
                  onClick={() => handleSelect(mapsLinkValue)}
                  role="option"
                  aria-selected={value === mapsLinkValue}
                >
                  {mapsLinkLabel}
                </button>
              )}
              <button
                type="button"
                className={`mama-gf-option mama-gf-option-other ${value === "OTHER" ? "is-selected" : ""}`}
                onClick={() => handleSelect("OTHER")}
                role="option"
                aria-selected={value === "OTHER"}
              >
                {otherLabel}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LocationDropdown;
